import { FC, useCallback, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import {
  giftCouponByIdDataAtomFamily,
  giftCouponByIdStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import { requestRenewal } from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponByIdHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useNavigate, useParams } from 'react-router-dom';

import { ContactEmailLink } from '../components/ContactEmailLink';
import PhoneVerificationSection from './giftCouponRedeem/PhoneVerificationSection';
import SignatureModal from './giftCouponRedeem/SignatureModal';

const GiftCouponRenewalPage: FC = function GiftCouponRenewalPage() {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();

  useGiftCouponByIdHook(couponId || '', !couponId);

  const status = useRecoilValue(giftCouponByIdStatusAtomFamily(couponId || ''));
  const coupon = useRecoilValue(giftCouponByIdDataAtomFamily(couponId || ''));
  console.log(coupon);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [requesterName, setRequesterName] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmitClick = useCallback(() => {
    if (!requesterName.trim()) {
      setSubmitError('본인 성함을 입력해 주세요.');
      return;
    }
    if (!isVerified) {
      setSubmitError('본인 인증을 완료해 주세요.');
      return;
    }
    setSubmitError(null);
    setIsSignatureModalOpen(true);
  }, [isVerified, requesterName]);

  const handleSignatureConfirm = useCallback(
    async (signatureDataUrl: string) => {
      if (!couponId) return;
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        await requestRenewal(couponId, {
          phoneNumber: phoneNumber.replace(/-/g, ''),
          name: requesterName.trim(),
          signatureImage: signatureDataUrl,
        });
        setSubmitSuccess(true);
        setIsSignatureModalOpen(false);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '재발급 신청 중 오류가 발생했습니다.';
        setSubmitError(message);
        setIsSignatureModalOpen(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [couponId, phoneNumber, requesterName],
  );

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50">
        <Loading />
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <ExclamationCircleIcon className="h-16 w-16 text-stone-300" />
        <h2 className="text-lg font-bold text-stone-700">쿠폰을 찾을 수 없습니다</h2>
        <p className="text-center text-sm text-stone-500">
          유효하지 않은 링크이거나, 이미 처리된 쿠폰입니다.
        </p>
      </div>
    );
  }

  if (!coupon.issued || coupon.issued.type !== 'renewal') {
    const couponCode = coupon.code;
    if (couponCode) {
      navigate(`/gift-store/coupon/${couponCode}`, { replace: true });
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <ExclamationCircleIcon className="h-16 w-16 text-stone-300" />
        <h2 className="text-lg font-bold text-stone-700">
          재발급 대상이 아닌 쿠폰입니다
        </h2>
        <p className="text-center text-sm text-stone-500">
          재발급이 승인된 경우에만 이 페이지에 접근할 수 있습니다.
        </p>
      </div>
    );
  }

  if (coupon.issued.requestedAt) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <CheckCircleSolidIcon className="h-16 w-16 text-green-500" />
        <h2 className="text-lg font-bold text-stone-700">
          이미 재발급 신청이 완료되었습니다
        </h2>
        <p className="text-center text-sm text-stone-500">
          쿠폰 재발급이 진행 중입니다. 잠시만 기다려 주세요.
        </p>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-50 px-6">
        <CheckCircleSolidIcon className="h-16 w-16 text-green-500" />
        <h2 className="text-lg font-bold text-stone-700">재발급 신청이 완료되었습니다</h2>
        <p className="text-center text-sm text-stone-500">
          보내주신 분의 마음이 담긴 새 쿠폰이 곧 발급됩니다.
          <br />
          이번에는 꼭 사용해 주세요.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-8">
      <div className="mx-auto max-w-md">
        {/* 헤더 */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-800">답례품 쿠폰 재발급 안내</h1>
          <p className="mt-2 text-sm text-stone-500">
            유효기간이 지나 사용하지 못한 쿠폰에 대해,
            <br />
            보내주신 분의 배려로 한 번 더 교환 기회를 드립니다.
          </p>
        </div>

        {/* 선택 상품 정보 */}
        {coupon.selectedChoiceUnit &&
          typeof coupon.selectedChoiceUnit === 'object' &&
          'name' in coupon.selectedChoiceUnit && (
            <div className="mt-6 overflow-hidden rounded-xl border border-stone-200">
              <div className="relative h-40 w-full bg-stone-200">
                {coupon.selectedChoiceUnit.image &&
                typeof coupon.selectedChoiceUnit.image === 'object' &&
                'url' in coupon.selectedChoiceUnit.image ? (
                  <img
                    src={fileBucketUrl(coupon.selectedChoiceUnit.image.url, 'w400')}
                    alt={coupon.selectedChoiceUnit.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <svg
                      className="h-10 w-10 text-stone-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-8">
                  <p className="text-sm font-semibold text-white">
                    {coupon.selectedChoiceUnit.name}
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* 안내사항 */}
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2">
            <ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-xs leading-relaxed text-amber-800">
              <p className="font-bold">꼭 읽어주세요</p>
              <ul className="mt-1.5 list-disc space-y-1 break-keep pl-4">
                <li>
                  이 재발급은 보내주신 분의 특별한 배려로 <strong>단 1회</strong>에 한해
                  진행됩니다.
                </li>
                <li>
                  기존과 <strong>동일한 품목</strong>으로만 교환 가능합니다.
                </li>
                <li>
                  재발급 후 꼭 <strong>안내 받으신 유효기간내에</strong>에 사용하셔야
                  합니다.
                </li>
                <li className="font-semibold text-red-700">
                  재발급 쿠폰 만료 후에는 어떠한 사유로도 추가 발급이 불가합니다.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* STEP 1: 본인 인증 */}
        <PhoneVerificationSection
          couponId={couponId || ''}
          isSelectable={false}
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          isVerified={isVerified}
          onVerified={() => setIsVerified(true)}
          onVerificationReset={() => setIsVerified(false)}
          submitError={null}
          isSubmitting={isSubmitting}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSubmit={() => {}}
          hideSubmitButton
          showRequesterName
          requesterName={requesterName}
          onRequesterNameChange={setRequesterName}
        />

        {/* STEP 2: 서명 및 동의 */}
        {isVerified && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-center gap-2">
              <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
                STEP 2
              </span>
            </div>

            <h3 className="text-center text-lg font-bold text-stone-700">
              재발급 신청 확인
            </h3>
            <p className="mt-1.5 text-center text-sm text-stone-400">
              아래 내용을 확인하시고 서명 후 신청해 주세요
            </p>

            {/* 동의 내용 박스 */}
            <div className="mt-5 rounded-lg border border-stone-200 bg-stone-50 p-4 text-xs leading-relaxed text-stone-600">
              <p className="font-bold text-stone-700">신청 확인사항</p>
              <ul className="mt-2 list-disc space-y-1.5 pl-4">
                <li>보내주신 분의 배려로 제공되는 특별 재발급임을 이해합니다.</li>
                <li>재발급 쿠폰의 유효기간 내 반드시 사용하겠습니다.</li>
                <li className="font-semibold text-red-600">
                  유효기간 내 미사용 시 추가 재발급이 불가함을 확인합니다.
                </li>
                <li>재발급 신청 후 취소가 어려울 수 있음을 확인합니다.</li>
              </ul>
            </div>

            {submitError && (
              <p className="mt-4 text-center text-sm text-red-500">{submitError}</p>
            )}

            {/* 연장 신청 버튼 */}
            <button
              onClick={handleSubmitClick}
              disabled={isSubmitting}
              className={`mt-6 w-full rounded-lg py-4 text-base font-bold transition ${
                isSubmitting
                  ? 'bg-stone-200 text-stone-400'
                  : 'bg-violet-600 text-white active:bg-violet-700'
              }`}
            >
              {isSubmitting ? '처리 중...' : '확인 및 재발급 신청하기'}
            </button>
          </div>
        )}

        {/* 하단 연락처 */}
        <div className="mt-8 text-center">
          <ContactEmailLink />
        </div>
      </div>

      {/* 서명 모달 */}
      <SignatureModal
        open={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onConfirm={handleSignatureConfirm}
        isSubmitting={isSubmitting}
        title="특별 재발급 쿠폰 신청을 위한 서명"
      />
    </div>
  );
};

export default GiftCouponRenewalPage;
